import { Coins } from 'lucide-react';
import type { TokenUsage } from '@/hooks/useAi';

interface Props {
  usage: TokenUsage;
  className?: string;
  /** Kompakt: nur Cost + Total-Tokens. Default: full. */
  compact?: boolean;
}

/**
 * TokenStatsBadge - kompakter Indicator für KI-Token-Verbrauch.
 *
 * Zeigt: Cost ($) + Input/Output (mit Cache-Anteil) + Cache-Hit-Rate.
 * Bei compact=true reduziert auf "$X.XX • N.NK Tok".
 */
export function TokenStatsBadge({ usage, className, compact = false }: Props): JSX.Element {
  const totalInput = usage.input_tokens + usage.cache_read_tokens;
  const totalOutput = usage.output_tokens;
  const cacheHitRate =
    totalInput > 0 ? Math.round((usage.cache_read_tokens / totalInput) * 100) : 0;

  if (compact) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1 rounded-md border border-border bg-surface-2/50 px-2 py-0.5 text-xs',
          className ?? '',
        ].join(' ')}
        data-testid="token-stats-badge"
        title={`${usage.input_tokens} input + ${usage.cache_read_tokens} cached + ${usage.output_tokens} output`}
      >
        <Coins className="size-3 text-muted" aria-hidden="true" />
        <span className="font-mono">{formatUsd(usage.cost_usd)}</span>
        <span className="text-muted">·</span>
        <span className="font-mono text-muted">{formatTokens(totalInput + totalOutput)} Tok</span>
      </span>
    );
  }

  return (
    <div
      className={[
        'inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border bg-surface-2/50 px-3 py-1.5 text-xs',
        className ?? '',
      ].join(' ')}
      data-testid="token-stats-badge"
    >
      <span className="inline-flex items-center gap-1">
        <Coins className="size-3 text-muted" aria-hidden="true" />
        <span className="font-mono font-medium">{formatUsd(usage.cost_usd)}</span>
        <span className="text-muted">verbraucht</span>
      </span>
      <span className="text-border" aria-hidden="true">
        |
      </span>
      <span className="text-muted">
        <span className="font-mono">{formatTokens(usage.input_tokens + usage.cache_read_tokens)}</span>{' '}
        input{' '}
        {usage.cache_read_tokens > 0 && (
          <>
            (cached:{' '}
            <span className="font-mono">{formatTokens(usage.cache_read_tokens)}</span>,{' '}
            {cacheHitRate}%)
          </>
        )}
      </span>
      <span className="text-border" aria-hidden="true">
        |
      </span>
      <span className="text-muted">
        <span className="font-mono">{formatTokens(usage.output_tokens)}</span> output
      </span>
    </div>
  );
}

function formatUsd(value: number): string {
  if (value < 0.001) return '<$0.001';
  if (value < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(1)}K`;
}
